import axios from 'axios';
import { API_URL } from '../config';
import { normalizePricingCatalog } from './pricingUtils';
import { slugToInternal } from './pricingUtils';

const billing = `${API_URL}/billing`;

const PRICING_URLS = [
  `${API_URL}/pricing/plans`,
  `${API_URL}/pricing/catalog`,
  `${billing}/plans`,
];

/** Public catalog — plans + add-ons (tries multiple endpoints for compatibility). */
export async function fetchPricingPlans() {
  let lastError;
  for (const url of PRICING_URLS) {
    try {
      const { data } = await axios.get(url);
      return normalizePricingCatalog(data);
    } catch (err) {
      lastError = err;
      const status = err.response?.status;
      if (status && status !== 404) {
        throw err;
      }
    }
  }
  throw lastError || new Error('Pricing catalog unavailable');
}

export async function fetchDisplaySettings() {
  try {
    const { data } = await axios.get(`${API_URL}/plans/display-settings`);
    return data;
  } catch {
    const catalog = await fetchPricingPlans().catch(() => null);
    return {
      payments_enabled: catalog?.payments_enabled ?? false,
      stripe_enabled: catalog?.stripe_enabled ?? false,
      contact_sales_email: catalog?.contact_sales_email ?? null,
    };
  }
}

export async function fetchBillingSubscription(organizationId) {
  const { data } = await axios.get(`${billing}/subscription`, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function fetchPlanContext(organizationId) {
  const { data } = await axios.get(`${API_URL}/plan/context`, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function previewProration(organizationId, { planSlug, billingCycle, addonId } = {}) {
  const params = { organization_id: organizationId };
  if (planSlug) params.new_plan_id = slugToInternal(planSlug);
  if (billingCycle) params.new_cycle = billingCycle;
  if (addonId) params.new_addon_id = addonId;
  const { data } = await axios.get(`${billing}/proration-preview`, { params });
  return data;
}

export async function previewDowngrade(organizationId, planSlug) {
  const { data } = await axios.get(`${billing}/downgrade-preview`, {
    params: {
      organization_id: organizationId,
      target_plan_id: slugToInternal(planSlug),
    },
  });
  return data;
}

export async function scheduleDowngrade(organizationId, planSlug, billingCycle) {
  const { data } = await axios.patch(`${billing}/subscription/plan`, {
    organization_id: organizationId,
    plan_id: slugToInternal(planSlug),
    billing_cycle: billingCycle,
  });
  return data;
}

export async function cancelScheduledDowngrade(organizationId) {
  const { data } = await axios.delete(`${billing}/subscription/downgrade-request`, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function upgradePlan(organizationId, planSlug, billingCycle) {
  const { data } = await axios.patch(`${billing}/subscription/plan`, {
    organization_id: organizationId,
    plan_id: slugToInternal(planSlug),
    billing_cycle: billingCycle,
  });
  return data;
}

export async function switchBillingCycle(organizationId, billingCycle) {
  const { data } = await axios.patch(`${billing}/subscription/cycle`, {
    organization_id: organizationId,
    billing_cycle: billingCycle,
  });
  return data;
}

export async function cancelSubscription(organizationId) {
  const { data } = await axios.delete(`${billing}/subscription`, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function reactivateSubscription(organizationId) {
  const { data } = await axios.post(`${billing}/subscription/reactivate`, null, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function startCheckout(organizationId, payload) {
  const { data } = await axios.post(`${billing}/subscription`, {
    organization_id: organizationId,
    plan_id: payload.planSlug ? slugToInternal(payload.planSlug) : payload.plan_id,
    billing_cycle: payload.billingCycle || payload.billing_cycle || 'monthly',
    addon_ids: payload.addonIds || [],
    addon_quantities: payload.addonQuantities || {},
  });
  return data;
}

export async function startSkipCashPayment(organizationId, payload) {
  const body = { ...payload };
  if (body.planSlug) {
    body.plan_id = slugToInternal(body.planSlug);
    delete body.planSlug;
  }
  const { data } = await axios.post(
    `${API_URL}/organizations/${organizationId}/payments/checkout`,
    body
  );
  return data;
}

/** Plan upgrade and/or new add-ons checkout (SkipCash). */
export async function startCatalogCheckout(organizationId, {
  planSlug,
  billingCycle,
  addonLines,
  addonsOnly = false,
}) {
  const lines = (addonLines || []).map(({ addonId, quantity }) => ({
    addon_id: addonId,
    quantity: quantity || 1,
  }));
  if (addonsOnly || !planSlug) {
    const { data } = await axios.post(
      `${API_URL}/organizations/${organizationId}/payments/checkout`,
      {
        product_type: 'catalog_addons',
        billing_cycle: billingCycle,
        addon_lines: lines,
      }
    );
    return data;
  }
  const { data } = await axios.post(
    `${API_URL}/organizations/${organizationId}/payments/checkout`,
    {
      product_type: 'plan',
      plan_id: slugToInternal(planSlug),
      billing_cycle: billingCycle,
      addon_lines: lines.length ? lines : undefined,
    }
  );
  return data;
}

export async function cancelCatalogAddon(organizationId, addonId) {
  const { data } = await axios.patch(
    `${API_URL}/organizations/${organizationId}/subscription/addons/${addonId}/cancel`
  );
  return data;
}

export async function setAddonAutoRenew(organizationId, addonId, enabled) {
  const { data } = await axios.patch(
    `${API_URL}/organizations/${organizationId}/subscription/addons/${addonId}/auto-renew`,
    { enabled }
  );
  return data;
}

export async function setPlanAutoRenew(organizationId, enabled) {
  const { data } = await axios.patch(
    `${API_URL}/organizations/${organizationId}/subscription/auto-renew`,
    { enabled }
  );
  return data;
}

export async function fetchAvailableAddons(organizationId) {
  const { data } = await axios.get(`${billing}/addons/available`, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function activateAddon(organizationId, addonId, { quantity = 1 } = {}) {
  const { data } = await axios.post(`${billing}/addons/${addonId}/activate`, {
    organization_id: organizationId,
    quantity,
  });
  return data;
}

export async function updateAddonQuantity(organizationId, addonId, quantity) {
  const { data } = await axios.patch(`${billing}/addons/${addonId}/quantity`, {
    organization_id: organizationId,
    quantity,
  });
  return data;
}

export async function scheduleAddonRemoval(organizationId, addonId) {
  const { data } = await axios.delete(`${billing}/addons/${addonId}`, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function fetchInvoices(organizationId, limit = 20) {
  const { data } = await axios.get(`${billing}/invoices`, {
    params: { organization_id: organizationId, limit },
  });
  return data;
}

export async function fetchBillingActivity(organizationId, limit = 50) {
  const { data } = await axios.get(`${billing}/activity`, {
    params: { organization_id: organizationId, limit },
  });
  return data;
}

export async function fetchPaymentMethods(organizationId) {
  const { data } = await axios.get(`${billing}/payment-methods`, {
    params: { organization_id: organizationId },
  });
  return data;
}

export async function pollPaymentOrder(orderId) {
  const { data } = await axios.get(`${API_URL}/payments/orders/${orderId}`);
  return data;
}
